/**
 * The local `base_seq` ledger (IR-126 §5.1, phase 3.2a).
 *
 * `base_seq` is the revision a command was composed against. The apply layer
 * allocates `seq = base_seq + 1` and refuses anything else as `STALE`. The
 * server value reaches the browser through the task document's
 * `last_revision_seq` — which means it is only correct **between** the user's
 * own actions, not during them.
 *
 * **The problem this exists for.** A user drags a card and immediately edits
 * it. The drag's command is still in flight; the task snapshot still reads
 * `last_revision_seq: 3`. So the edit is composed against 3 — the same value
 * the drag used — and the apply layer refuses it as `STALE`, correctly, having
 * just allocated seq 4. Nothing is wrong on the server. The user changed one
 * thing, then another, and was told someone else had edited their task.
 *
 * Reproducible every time, and §5.1 calls it out precisely because it reads as
 * a backend bug when it is a client one.
 *
 * **The fix.** Each in-flight command will produce exactly one revision, so the
 * client can predict the seq its next command should target: the server value
 * advanced by one per command it has outstanding. That is all this module does.
 *
 * ## The chain, and what a failure does to it (3.2b)
 *
 * When a command fails, the commands *behind* it were composed against a seq
 * that will now never exist, so the apply layer refuses each of them as `STALE`
 * in turn. Reported as they come, one rejected edit becomes a run of "someone
 * changed this" — errors describing a conflict that never happened, whose real
 * cause was the first edit not landing.
 *
 * So the commands in flight on a task carry a **chain id**. A failure bumps it,
 * which voids everything still outstanding: a voided command that is *itself
 * refused* settles as `discarded` rather than as a failure of its own, and the
 * prediction they rested on is thrown away so the next command re-bases from
 * the server (§5.1).
 *
 * Discarded still means rolled back — a refusal rolls back like any other. What
 * it does not mean is a second thing to tell the user about.
 *
 * Being in a voided chain is not by itself an outcome. A voided command that
 * times out is still `unresolved`, and one that applies anyway — the server can
 * move under it — is still `done`. Overwriting either would claim something the
 * chain never established.
 *
 * ## What it deliberately does NOT do
 *
 * Nothing here *renders* any of this. `unresolved` and `discarded` both need a
 * pending/rollback treatment on screen, and that is 3.2d; the §4.5 branch per
 * error class is 3.3.
 *
 * The ledger is in-memory and per-tab. It does not survive reload — §7.2.2
 * requires exactly that, because the server is authoritative after a reload
 * and a persisted guess would outrank it. Recovery is 3.2c, via
 * {@link rebaseTask}.
 */
import { logger } from '@/utils/logger'
import { isNotQueued, submitTaskCommand } from './useTaskCommands'
import { forgetCommand, rememberCommand } from './taskCommandPointers'
import type {
  SubmitOptions,
  TaskArchivePayload,
  TaskCommandOutcome,
  TaskEditPayload,
  TaskReapprovePayload,
  TaskUnapprovePayload,
} from './useTaskCommands'

/**
 * A submission's outcome, plus the one status the ledger adds (§5.1, 3.2b).
 *
 * `discarded` means: this command was **refused**, and an earlier command in
 * its chain had already failed — so it was composed against a seq that will
 * never exist and its own refusal says nothing new. The optimistic value has
 * been rolled back, as for any refusal; what the caller must not do is raise a
 * second error. One per discarded command is the cascade §5.1 exists to
 * prevent: a run of "someone changed this" whose real cause was the first edit
 * not landing.
 *
 * It replaces a refusal and **only** a refusal. An `unresolved` or `done`
 * outcome in a voided chain is reported as itself — see `submitSequencedCommand`
 * for why each of those would be a lie in a different direction.
 */
export interface SequencedOutcome extends Omit<TaskCommandOutcome, 'status'> {
  status: TaskCommandOutcome['status'] | 'discarded'
  /**
   * The `base_seq` this command was actually composed against.
   *
   * Not the value the caller passed: the ledger may have advanced it past the
   * server's for an earlier in-flight command. Exposed because it is the only
   * way a caller can tell whether the task has since moved past its own
   * submission — which is how an `unresolved` command stops being unresolved
   * (3.2d) and what a `stale` conflict has to show the user (3.3).
   */
  baseSeq: number
  /**
   * What voided this command. Set only when `discarded`.
   *
   * Usually the id of the command that failed — but it can be
   * {@link UNWRITTEN_COMMAND}, because a command that never reached Firestore
   * never got an id to name. Anything rendering this must not assume it can
   * look the value up as a document.
   */
  discardedBy?: string
}

/**
 * `discardedBy` when the voiding command was never written.
 *
 * `addDoc` failed, so there is no document and no id — the sentinel says
 * "the thing that broke this chain has no id", which is different from "we do
 * not know what broke it". Exported so callers match on a name rather than
 * hard-coding the string.
 */
export const UNWRITTEN_COMMAND = 'not-queued'

/**
 * A command the ledger can sequence: the four that target an existing task.
 *
 * `task_create` is absent by construction, not by omission — it has no
 * `base_seq` to advance (§5.1), and routing it through here would mean
 * inventing one. Its successor edit reads seq 0 from the server like any other
 * first edit.
 */
export type SequencedPayload =
  | Omit<TaskEditPayload, 'base_seq'>
  | Omit<TaskUnapprovePayload, 'base_seq'>
  | Omit<TaskReapprovePayload, 'base_seq'>
  | Omit<TaskArchivePayload, 'base_seq'>

/**
 * Two numbers, deliberately, because they are two different kinds of claim and
 * only one of them survives its command.
 *
 * An earlier version tracked one number plus a `confirmed` boolean. That cannot
 * work: the flag described *the entry*, but the number underneath it was
 * overwritten by the next reservation, so a prediction inherited the
 * trustworthiness of a value it had replaced. A failed command following an
 * applied one then stranded the ledger on a seq the server would never reach —
 * and since every retry composed against it and was refused `STALE`, nothing
 * could move the server to catch up. Deadlock until reload.
 */
interface Reservation {
  /**
   * What the next command should target, given the commands outstanding.
   *
   * A **prediction**. It assumes every in-flight command applies, which is
   * exactly what may not happen, so it is only meaningful while `inFlight > 0`.
   */
  predicted: number
  /** How many commands are outstanding. */
  inFlight: number
  /**
   * The highest seq the apply layer has **reported** allocating on this task.
   *
   * A fact, not a forecast: the task holds this value and the snapshot carrying
   * it is merely late. This is what may outlive its command, and the only thing
   * that may.
   */
  confirmedSeq?: number
  /**
   * Which chain the commands currently in flight belong to (§5.1, phase 3.2b).
   *
   * Bumped when a command fails, which is what **voids** the commands behind
   * it: each was composed against a seq the failure means will never exist, so
   * each is destined to be refused `STALE` in turn. A command that settles
   * carrying a chain id other than this one is reported as `discarded` rather
   * than as its own separate failure.
   */
  chain: number
  /** The command whose failure voided the previous chain, for reporting. */
  discardedBy?: string
}

const ledger = new Map<string, Reservation>()

const key = (projectId: string, taskId: string) => `${projectId}/${taskId}`

/**
 * The `base_seq` a command on this task should carry.
 *
 * `serverSeq` is the task's `last_revision_seq` (use `ProjectTask.revisionSeq`,
 * which defaults absent to 0).
 *
 * Takes the **larger** of the two rather than preferring the reservation. The
 * server can legitimately move ahead of our prediction — a teammate's edit, or
 * our own command applying and its snapshot landing before we settle the
 * reservation. Preferring the local value there would compose against a seq
 * already consumed and turn a real concurrent edit into a silent overwrite
 * attempt; taking the max means we still lose that race, but we lose it as a
 * `STALE` the user is shown, which is what §5.1 asks for.
 *
 * **Self-evicting.** A confirmed entry is kept after its command settles,
 * because `serverSeq` reaches the browser on the *task* document's listener
 * while the outcome arrives on the *command* document's — two independent
 * snapshots of one transaction, with no ordering guarantee between them. Once
 * `serverSeq` has caught up the entry can only agree with it, so it is dropped
 * here. That is what stops the ledger growing for the life of the tab.
 */
export function nextBaseSeq(projectId: string, taskId: string, serverSeq: number): number {
  const k = key(projectId, taskId)
  const held = ledger.get(k)
  if (!held) return serverSeq

  // With commands outstanding, the prediction is live — but a reported seq can
  // already exceed it (a sibling command applied further ahead than we
  // forecast), and a fact always outranks a forecast.
  const anchor =
    held.inFlight > 0
      ? Math.max(held.predicted, held.confirmedSeq ?? held.predicted)
      : held.confirmedSeq

  // Belt and braces: `settle` already drops an entry that reaches zero
  // in-flight with nothing reported, so this is unreachable today. Kept because
  // the alternative — falling back to `predicted` — is the deadlock described
  // above, and a future edit that lets such an entry survive should land on the
  // server value rather than on a forecast nothing ever realised.
  if (anchor === undefined) {
    ledger.delete(k)
    return serverSeq
  }

  if (held.inFlight === 0 && serverSeq >= anchor) {
    ledger.delete(k)
    return serverSeq
  }
  return Math.max(serverSeq, anchor)
}

/**
 * Drop the local prediction for a task and fall back to the server value.
 *
 * The seam 3.2b (discard-the-chain) and 3.2c (reload recovery) both need. Also
 * correct on its own: after any outcome the client cannot explain, the server
 * value is the only one worth trusting.
 */
export function rebaseTask(projectId: string, taskId: string): void {
  ledger.delete(key(projectId, taskId))
}

/** Test seam. Never call from application code. */
export function _resetLedger(): void {
  ledger.clear()
}

/** Test seam — proves entries are evicted rather than accumulating. */
export function _ledgerSize(): number {
  return ledger.size
}

/** Reserve the next seq for a command about to be submitted; returns its chain id. */
function reserve(projectId: string, taskId: string, baseSeq: number): number {
  const k = key(projectId, taskId)
  const held = ledger.get(k)
  ledger.set(k, {
    // One command, one revision — so the next one targets the seq this command
    // is about to allocate. A fresh prediction every time: this command may not
    // apply, and nothing about a previous command makes that more likely.
    predicted: baseSeq + 1,
    inFlight: (held?.inFlight ?? 0) + 1,
    // Carried, because it is a fact about the task and submitting another
    // command does not un-report it.
    confirmedSeq: held?.confirmedSeq,
    chain: held?.chain ?? 0,
    discardedBy: held?.discardedBy,
  })
  return ledger.get(k)!.chain
}

/**
 * Void the current chain: the commands still in flight behind a failure.
 *
 * `failedCommandId` is {@link UNWRITTEN_COMMAND} when the failure was a write
 * that never happened, since there is no document to name.
 *
 * Each was composed against a seq the failure means will never exist, so each
 * is already destined to be refused `STALE`. Bumping the chain id is what lets
 * their outcomes be reported as `discarded` — one error for the user instead of
 * a run of "someone changed this" whose real cause was the first edit not
 * landing (§5.1).
 *
 * It also throws away the prediction. Those commands will produce no revisions,
 * so a forecast that counted on them is worthless; falling back to the
 * confirmed seq — or, through `nextBaseSeq`, to the server — is what "re-bases
 * from the server value" means.
 */
function voidChain(projectId: string, taskId: string, failedCommandId: string): void {
  const k = key(projectId, taskId)
  const held = ledger.get(k)
  if (!held) {
    // The failure settled last, so nothing is left behind it to void. Nothing
    // to record either — the entry is gone and the next command re-bases on the
    // server, which is what voiding would have arranged anyway.
    return
  }
  ledger.set(k, {
    ...held,
    chain: held.chain + 1,
    predicted: held.confirmedSeq ?? 0,
    discardedBy: failedCommandId,
  })
}

function chainOf(projectId: string, taskId: string): number | undefined {
  return ledger.get(key(projectId, taskId))?.chain
}

function discardedByOf(projectId: string, taskId: string): string | undefined {
  return ledger.get(key(projectId, taskId))?.discardedBy
}

/**
 * Account for a command that is no longer in flight.
 *
 * `producedSeq` is the seq the apply layer actually allocated, when it applied.
 * It is a **fact about the task**, not a prediction, and outranks both.
 *
 * Note it raises `next` to `producedSeq`, **not** `producedSeq + 1`: the apply
 * layer sets `last_revision_seq = producedSeq`, and `base_seq` must equal
 * `last_revision_seq`. Adding one here would hand the next command a seq the
 * server has not reached and refuse it as `STALE` — the precise bug this module
 * exists to prevent, reintroduced at the other end.
 */
function settle(projectId: string, taskId: string, producedSeq?: number): void {
  const k = key(projectId, taskId)
  const held = ledger.get(k)
  if (!held) return

  const inFlight = Math.max(0, held.inFlight - 1)
  const confirmedSeq =
    producedSeq === undefined
      ? held.confirmedSeq
      : Math.max(held.confirmedSeq ?? producedSeq, producedSeq)

  if (inFlight === 0 && confirmedSeq === undefined) {
    ledger.delete(k)
    return
  }

  ledger.set(k, {
    predicted: held.predicted,
    inFlight,
    confirmedSeq,
    chain: held.chain,
    discardedBy: held.discardedBy,
  })
}

/**
 * Submit a command with its `base_seq` supplied by the ledger.
 *
 * Every outcome releases the reservation; they differ only in what is known:
 *
 * - **done** — reconcile against the seq actually allocated.
 * - **failed** — no revision was produced.
 * - **unresolved** — we do not know whether one was.
 *
 * **Why `unresolved` releases rather than holds.** Holding looks safer — the
 * command may still apply, so the seq may still be taken. But the two mistakes
 * are not equally recoverable. Hold, and if the command never applies, the
 * server never moves; `nextBaseSeq` keeps returning the advanced value and
 * *every* later command on that task is refused `STALE` until the tab is
 * reloaded, with nothing to explain why. Release, and if the command does apply
 * late, exactly one command is refused `STALE` — and the retry after it
 * succeeds, because the server has moved by then and `nextBaseSeq` reads the
 * larger value.
 *
 * One self-correcting refusal beats a stuck task, so this releases.
 *
 * A throw (not signed in, the write failed, the listener died) releases too:
 * `submitTaskCommand` rejects only where no command is in flight to protect.
 */
export async function submitSequencedCommand(
  projectId: string,
  payload: SequencedPayload,
  serverSeq: number,
  options: SubmitOptions = {},
): Promise<SequencedOutcome> {
  const taskId = payload.task_id
  const baseSeq = nextBaseSeq(projectId, taskId, serverSeq)
  const chain = reserve(projectId, taskId, baseSeq)

  let outcome: TaskCommandOutcome
  try {
    outcome = await submitTaskCommand(
      projectId,
      { ...payload, base_seq: baseSeq } as Parameters<typeof submitTaskCommand>[1],
      {
        ...options,
        // Recorded the moment the document exists, so a reload a second later
        // can still find it (§7.2.2, 3.2c). Written here rather than in
        // `useTaskCommands` because `baseSeq` is this module's to know.
        onQueued: (commandId) => {
          rememberCommand({ projectId, taskId, commandId, baseSeq })
          options.onQueued?.(commandId)
        },
      },
    )
  } catch (e) {
    // A throw before the write means there is no id and nothing was recorded;
    // a throw after it means the listener died, and the pointer is exactly what
    // lets a later load pick the command back up. Neither case forgets.
    const chainAtThrow = chainOf(projectId, taskId)
    settle(projectId, taskId)
    if (isNotQueued(e)) {
      // The command never reached Firestore, so nothing will ever apply it —
      // and everything composed against it is already doomed to STALE. Exactly
      // the cascade a `failed` outcome voids the chain to prevent, arriving by
      // a different route (§5.1).
      //
      // A listener that died AFTER the write is deliberately not this: the
      // command is queued and may well apply, so voiding would discard commands
      // that are about to become perfectly valid. Same reasoning as
      // `unresolved` — the outcome is unobservable, not known to be nothing.
      if (chainAtThrow !== undefined) voidChain(projectId, taskId, UNWRITTEN_COMMAND)
    }
    // A voided command still throws rather than being absorbed as discarded.
    // A listener death is a FAULT, not an outcome: the caller has to handle the
    // exception whatever the chain did, and swallowing it into a status would
    // hide a broken subscription behind a routine-looking result. The
    // asymmetry with the success path above is deliberate.
    throw e
  }

  if (outcome.status === 'unresolved') {
    // Worth a line: if the command does apply later, the next command on this
    // task is refused STALE once, and this is the only record of why.
    logger.info(
      `task ${taskId}: command ${outcome.commandId} unresolved, releasing base_seq ${baseSeq}`,
    )
  }

  // Read BEFORE settling, because `settle` drops the entry once nothing is
  // outstanding and nothing was confirmed — which is exactly the state a chain
  // of failures ends in. Read afterwards, the last command in a voided chain
  // would find no entry and report itself as a fresh failure, producing the
  // second error this card exists to remove.
  //
  // Read before this command's own failure is allowed to void the chain, too,
  // so the first failure to settle is reported as the failure and everything
  // after it as discarded — never two competing explanations for one cause.
  const chainAtSettle = chainOf(projectId, taskId)
  const discardedBy = discardedByOf(projectId, taskId)

  // Forgotten once the outcome is KNOWN — which `unresolved` is not.
  //
  // `done` and `failed` have been reported; there is nothing a later load could
  // add. An `unresolved` command is the opposite: its fate is precisely what
  // nobody has established, and the pointer is the only handle a later load has
  // on it. Drop it and this sequence loses the report entirely — command times
  // out at 15s, the badge says unconfirmed, the user reloads (taking the badge
  // with it, since the pending state is in memory), the command fails at 20s,
  // and nothing is listening and nothing is stored. The board shows the old
  // value and the user never learns their edit was refused.
  //
  // Re-reading the task on reload is not the answer to that: it gives the user
  // the value, and the missing thing is the reason.
  //
  // It cannot accumulate — `recoverOne` forgets the pointer in its `finally`
  // whatever the recovered command turns out to be — so keeping it costs at
  // most one 8s recovery attempt on the next load.
  if (outcome.status !== 'unresolved') forgetCommand(outcome.commandId)

  settle(projectId, taskId, outcome.status === 'done' ? outcome.seq : undefined)

  // An absent entry is not a voided chain: it is a task the ledger is no longer
  // tracking, which a lone failing command also produces.
  const voided = chainAtSettle !== undefined && chainAtSettle !== chain

  // `discarded` replaces a REFUSAL and nothing else. Chain membership says the
  // command was composed against a doomed seq; it does not say what became of
  // it, and the other two outcomes are not refusals:
  //
  //   unresolved — we stopped watching, which is not the same as nothing
  //     happening. `submitTaskCommand` rolls back only on `failed`, so the
  //     optimistic value is still on screen; calling it discarded tells the
  //     caller it never landed and needs no report, and the value sits there
  //     silently wrong. That is §7.2's worst case, and it is the same mistake
  //     3.2a rejected when it refused to hold a reservation for an unresolved
  //     command: unobservable is not known-to-be-nothing.
  //
  //   done — it APPLIED. A voided command can still apply if the server moved
  //     under it (a teammate's edit reaching the seq it was composed against).
  //     The value is on the server and correctly still on screen; reporting it
  //     discarded invites the caller to roll back a real revision.
  //
  // So both pass through untouched, and only a refusal is absorbed.
  if (voided && outcome.status === 'failed') {
    logger.info(
      `task ${taskId}: command ${outcome.commandId} discarded - an earlier command in its chain failed`,
    )
    return { ...outcome, status: 'discarded', baseSeq, discardedBy }
  }

  if (outcome.status === 'failed') {
    // Everything still in flight behind this was composed against a seq that
    // will now never exist. Void them here rather than letting each be refused
    // STALE and reported separately (§5.1).
    voidChain(projectId, taskId, outcome.commandId)
  }

  return { ...outcome, baseSeq }
}
